/**
 * One-page landscape print of the org chart. The print window has no access to
 * the app's CSS variables, so this is the one place the chart uses literal
 * colors: the light-theme SBG_HEX values, plus the categorical series read
 * from the live tokens (book §6.4, §13.3).
 */
import { readToken, SBG_HEX } from '../../../theme/sbgTokens';
import { formatCount, formatDateTime, formatNumber, initialsOf } from '../../../utils/format';
import {
  DensityMode,
  DepartmentSwatch,
  departmentKeyOf,
  OrgLayout,
  seriesSlotOf,
  titleLineOf,
} from './orgChartModel';

const escapePrintText = (value: string | number | null | undefined) =>
  String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

const truncatePrintText = (value: string | null | undefined, maxLength: number) => {
  const text = String(value || '').trim();
  if (text.length <= maxLength) return text;
  return `${text.slice(0, Math.max(0, maxLength - 1)).trimEnd()}…`;
};

const DISPLAY_STACK = '"Host Grotesk", "Instrument Sans", ui-sans-serif, system-ui, -apple-system, "Segoe UI", sans-serif';
const BODY_STACK = '"Instrument Sans", ui-sans-serif, system-ui, -apple-system, "Segoe UI", sans-serif';

const seriesHex = (slot: number | null) => (slot ? readToken(`chart-${slot}`, SBG_HEX.slate400) : SBG_HEX.slate400);

export const buildOrgChartPrintHtml = (params: {
  orgLayout: OrgLayout;
  densityMode: DensityMode;
  searchTerm: string;
  filterLabel?: string | null;
  stats: { total: number; managers: number; roots: number };
  departments: DepartmentSwatch[];
  generatedAt: Date;
}) => {
  const { orgLayout, densityMode, searchTerm, filterLabel, stats, departments, generatedAt } = params;
  const chartWidth = Math.max(orgLayout.width, 1);
  const chartHeight = Math.max(orgLayout.height, 1);
  const isManagerMode = densityMode === 'managers';
  const { cardWidth, cardHeight } = orgLayout;

  const linksMarkup = orgLayout.links
    .map((link) => `<path d="${escapePrintText(link.path)}" fill="none" stroke="${SBG_HEX.slate400}" stroke-width="1.25" stroke-linejoin="round" />`)
    .join('');

  const nodesMarkup = orgLayout.nodes
    .map((item) => {
      const employee = item.data.employee;
      if (!employee) return '';
      const slot = seriesSlotOf(departments, departmentKeyOf(employee));
      const departmentLabel = employee.department?.name || 'No department';
      const name = truncatePrintText(employee.name, isManagerMode ? 24 : 22);
      const title = truncatePrintText(titleLineOf(employee), isManagerMode ? 30 : 26);
      const department = truncatePrintText(departmentLabel, isManagerMode ? 32 : 28);
      const direct = item.data.directCount;
      const badge = direct > 0
        ? `<rect x="${cardWidth - 40}" y="12" width="28" height="18" rx="9" fill="${SBG_HEX.cloud}" stroke="${SBG_HEX.slate200}" />
           <text x="${cardWidth - 26}" y="25" text-anchor="middle" font-size="10" font-weight="500" fill="${SBG_HEX.slate700}">${escapePrintText(formatNumber(direct))}</text>`
        : '';
      const strip = isManagerMode
        ? `<line x1="12" x2="${cardWidth - 12}" y1="${cardHeight - 36}" y2="${cardHeight - 36}" stroke="${SBG_HEX.slate100}" />
           <text x="12" y="${cardHeight - 15}" font-size="10.5" fill="${SBG_HEX.slate600}">${escapePrintText(
             `${formatCount(direct, 'direct report')} · ${formatNumber(item.data.totalDescendants)} in org`,
           )}</text>`
        : '';

      return `
        <g transform="translate(${item.x}, ${item.y})">
          <rect x="0" y="0" width="${cardWidth}" height="${cardHeight}" rx="12" fill="${SBG_HEX.white}" stroke="${SBG_HEX.slate200}" />
          <circle cx="28" cy="28" r="16" fill="${SBG_HEX.blue50}" />
          <text x="28" y="32" text-anchor="middle" font-size="11" font-weight="600" fill="${SBG_HEX.blue700}">${escapePrintText(initialsOf(employee.name))}</text>
          <text x="52" y="25" font-size="13" font-weight="500" fill="${SBG_HEX.navy}">${escapePrintText(name)}</text>
          <text x="52" y="41" font-size="11" fill="${SBG_HEX.slate600}">${escapePrintText(title)}</text>
          ${badge}
          <circle cx="16" cy="66" r="4" fill="${seriesHex(slot)}" />
          <text x="26" y="70" font-size="11" fill="${SBG_HEX.slate600}">${escapePrintText(department)}</text>
          ${strip}
        </g>
      `;
    })
    .join('');

  const legendMarkup = departments
    .filter((swatch) => swatch.colored)
    .map(
      (swatch, index) =>
        `<span class="legend__item"><span class="legend__dot" style="background:${seriesHex(index + 1)}"></span>${escapePrintText(swatch.label)}</span>`,
    )
    .join('');

  const scopeParts = [
    isManagerMode ? 'Managers view' : 'All people',
    filterLabel ? escapePrintText(filterLabel) : null,
    searchTerm.trim() ? `Search “${escapePrintText(searchTerm.trim())}”` : null,
    `Printed ${escapePrintText(formatDateTime(generatedAt))}`,
  ].filter(Boolean);

  return `
    <!doctype html>
    <html lang="en">
      <head>
        <meta charset="utf-8" />
        <title>Org chart · SBG Funding</title>
        <style>
          @page { size: letter landscape; margin: 0.28in; }
          * { box-sizing: border-box; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          html, body { margin: 0; width: 100%; height: 100%; background: ${SBG_HEX.white}; color: ${SBG_HEX.navy}; font-family: ${BODY_STACK}; }
          .page { width: 10.44in; height: 7.44in; padding: 0.08in; display: grid; grid-template-rows: auto minmax(0, 1fr) auto; gap: 0.12in; overflow: hidden; }
          .header { display: flex; justify-content: space-between; gap: 0.22in; align-items: flex-end; padding-bottom: 0.1in; border-bottom: 1px solid ${SBG_HEX.slate100}; }
          .overline { color: ${SBG_HEX.slate600}; font-size: 8px; font-weight: 500; letter-spacing: 0.06em; text-transform: uppercase; margin-bottom: 4px; }
          h1 { margin: 0; color: ${SBG_HEX.navy}; font-family: ${DISPLAY_STACK}; font-size: 22px; font-weight: 500; line-height: 1.1; letter-spacing: -0.01em; }
          .meta { margin-top: 5px; color: ${SBG_HEX.slate600}; font-size: 9.5px; font-variant-numeric: tabular-nums; }
          .stats { display: grid; grid-template-columns: repeat(3, 0.9in); gap: 0.16in; }
          .stat { border-top: 1px solid ${SBG_HEX.slate200}; padding-top: 0.06in; }
          .stat__label { color: ${SBG_HEX.blue600}; font-size: 7px; font-weight: 500; letter-spacing: 0.06em; text-transform: uppercase; }
          .stat__value { margin-top: 3px; color: ${SBG_HEX.navy}; font-family: ${DISPLAY_STACK}; font-size: 16px; font-weight: 500; line-height: 1; font-variant-numeric: tabular-nums; }
          .chartWrap { min-height: 0; border: 1px solid ${SBG_HEX.slate100}; border-radius: 0.14in; background: ${SBG_HEX.slate25}; padding: 0.1in; overflow: hidden; display: flex; align-items: center; justify-content: center; }
          svg { display: block; width: 100%; height: 100%; max-height: 6.3in; font-family: ${BODY_STACK}; }
          .footer { display: flex; justify-content: space-between; align-items: center; gap: 0.2in; color: ${SBG_HEX.slate600}; font-size: 8px; font-variant-numeric: tabular-nums; border-top: 1px solid ${SBG_HEX.slate100}; padding-top: 0.06in; }
          .legend { display: flex; flex-wrap: wrap; align-items: center; gap: 0.06in 0.14in; }
          .legend__item { display: inline-flex; align-items: center; gap: 4px; }
          .legend__dot { width: 7px; height: 7px; border-radius: 50%; display: inline-block; }
        </style>
      </head>
      <body>
        <main class="page">
          <header class="header">
            <section>
              <div class="overline">SBG Funding · Workspace</div>
              <h1>Org chart</h1>
              <div class="meta">${scopeParts.join(' · ')}</div>
            </section>
            <section class="stats">
              <div class="stat"><div class="stat__label">People</div><div class="stat__value">${escapePrintText(formatNumber(stats.total))}</div></div>
              <div class="stat"><div class="stat__label">Managers</div><div class="stat__value">${escapePrintText(formatNumber(stats.managers))}</div></div>
              <div class="stat"><div class="stat__label">Top level</div><div class="stat__value">${escapePrintText(formatNumber(stats.roots))}</div></div>
            </section>
          </header>

          <section class="chartWrap">
            <svg viewBox="0 0 ${chartWidth} ${chartHeight}" preserveAspectRatio="xMidYMid meet" xmlns="http://www.w3.org/2000/svg" role="img" aria-label="SBG Funding org chart">
              ${linksMarkup}
              ${nodesMarkup}
            </svg>
          </section>

          <footer class="footer">
            <div class="legend">${legendMarkup}</div>
            <div>${escapePrintText(formatCount(orgLayout.nodes.length, 'card'))} on one landscape page</div>
          </footer>
        </main>
      </body>
    </html>
  `;
};
